import { trackApiPerformanceAddToCartActionTiming } from "@faire/web-api/events/apiPerformance/timing/addToCartAction";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { LoadTimingStatus } from "@faire/web/common/performance/userPerceivedLoadTiming/types";
import { TimingMarker } from "@faire/web/common/timings/TimingMarker";
import { useCallback, useEffect } from "react";

import { AddToCartVariants } from "@faire/retailer/stores/domain/CartStore/triggerAddToCartEvent";

import {
  ActionTiming,
  useAddToCartTrackingContext,
} from "./AddToCartTrackingContext";

type TrackingCartItem = {
  productOptionToken: string;
  quantity: number;
};

type AddToCartItem = {
  productOptionToken: string;
  quantity: number;
};

type StartTrackingArg = {
  items: TrackingCartItem[];
  variant: AddToCartVariants;
  startTime?: number;
};

type StopTrackingArg = {
  items: TrackingCartItem[];
};

type GenerateRefCallbackArg = {
  productOptionToken: string;
};

export const useAddToCartTracking = () => {
  const store = useAddToCartTrackingContext();

  const startTracking = useCallback(
    ({ items, startTime, variant }: StartTrackingArg) => {
      items.forEach(({ productOptionToken, quantity }) => {
        const key = buildKey(productOptionToken, quantity);
        store[key] = {
          startTime: new TimingMarker(startTime),
          variant: variant,
        };
      });
    },
    [store]
  );

  const trackTimingForMultipleKeys = useCallback(
    (keys: string[], status: LoadTimingStatus) => {
      const entries = Object.entries(store).filter(([k]) =>
        keys.some((key) => k === key)
      );
      let actionTiming: ActionTiming | undefined;
      for (const [key, value] of entries) {
        if (value) {
          // Use the timing of the first event
          if (value && !actionTiming) {
            actionTiming = value;
          }
          delete store[key];
        }
      }
      if (actionTiming?.startTime) {
        /**
         * We're currently using a temporary event to test this new implementation,
         * but we will replace it with UPLT in FD-208859.
         */
        trackApiPerformanceAddToCartActionTiming(
          { start: actionTiming.startTime },
          status,
          actionTiming?.variant ?? "none"
        );
      }
    },
    [store]
  );

  const stopTracking = useCallback(
    ({ items }: StopTrackingArg) => {
      const keys = items.map(({ productOptionToken, quantity }) =>
        buildKey(productOptionToken, quantity)
      );
      trackTimingForMultipleKeys(keys, "SUCCESS");
    },
    [trackTimingForMultipleKeys]
  );

  const trackTimingForMatchingKey = useCallback(
    (
      key: string,
      status: LoadTimingStatus,
      compareKeys?: (a: string, b: string) => boolean
    ) => {
      const entries: [string, ActionTiming | undefined][] = compareKeys
        ? Object.entries(store).filter(([k]) => compareKeys(k, key))
        : [[key, store[key]]];
      for (const [key, value] of entries) {
        if (value) {
          if (value?.startTime) {
            /**
             * We're currently using a temporary event to test this new implementation,
             * but we will replace it with UPLT in FD-208859.
             */
            trackApiPerformanceAddToCartActionTiming(
              { start: value.startTime },
              status,
              value.variant ?? "none"
            );
          }
          delete store[key];
        }
      }
    },
    [store]
  );

  const generateRefCallback: ({
    productOptionToken,
  }: GenerateRefCallbackArg) => React.RefCallback<HTMLElement> = useCallback(
    ({ productOptionToken }) => {
      const key = buildKey(productOptionToken);
      const refCallback = debounceIfPossible(
        (element: HTMLElement | null) => {
          if (!element) {
            trackTimingForMatchingKey(
              key,
              "TARGET_COMPONENT_UNMOUNTED",
              (a, b) => a.startsWith(b)
            );
          }
        },
        100,
        /**
         * Some components may unmount and remount quickly, so we
         * want to ensure that we always track the last event.
         */
        { trailing: true }
      );
      return refCallback;
    },
    [trackTimingForMatchingKey]
  );

  return {
    startTracking,
    stopTracking,
    generateRefCallback,
  };
};

export const useAddToCartTrackingDetector = (item: AddToCartItem) => {
  const { stopTracking, generateRefCallback } = useAddToCartTracking();

  useEffect(() => {
    stopTracking({ items: [item] });
  }, [item, stopTracking]);

  return generateRefCallback({
    productOptionToken: item.productOptionToken,
  });
};

const ADD_TO_CART_ACTION_KEY = "add-to-cart";

export const buildKey = (productOptionToken: string, quantity?: number) => {
  if (quantity) {
    return `${ADD_TO_CART_ACTION_KEY}-${productOptionToken}-${quantity}`;
  }
  return `${ADD_TO_CART_ACTION_KEY}-${productOptionToken}`;
};
